import React, { useState, useEffect, useRef } from "react";
import { getCookie } from "../../Utils/cookies";
import axios from "axios";
import { FaCheck, FaBan } from "react-icons/fa";

const VerifiedImageHistory = () => {
  const [installations, setInstallations] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedState, setSelectedState] = useState("Maharashtra");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const empData = getCookie("empData") ? JSON.parse(getCookie("empData")) : {};

  const gridRef = useRef(null);

  const states = [
    "Maharashtra",
    "Haryana",
    "Punjab",
    "Rajasthan",
    "Chhattisgarh",
  ];

  const itemsPerPage = 12;

  // ✅ Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Fetch Data
  useEffect(() => {
    const fetchVerifiedData = async () => {
      try {
        setLoading(true);
        setError(null);

        const department = empData?.department || "";

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/verified-installation-data`,
          {
            params: {
              state: selectedState,
              department,
              page: currentPage,
              limit: itemsPerPage,
            },
          },
        );

        if (response.data.success) {
          const apiData = response.data.data || [];
          setAllData(apiData);
          setTotalPages(response.data.totalPages || 1);
          setTotalRecords(response.data.totalCount || apiData.length);
        } else {
          throw new Error(response.data.message || "Failed to fetch data");
        }
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVerifiedData();
  }, [selectedState, empData?.department, currentPage]);

  // ✅ Filter by Saral ID
  useEffect(() => {
    let filtered = allData;

    if (debouncedSearch.trim() !== "") {
      filtered = allData.filter((item) =>
        String(item.farmerSaralId || "")
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase()),
      );
    }

    setInstallations(filtered);
  }, [debouncedSearch, allData]);

  // ✅ Download Excel – FIXED
  const downloadExcel = async () => {
    try {
      setLoading(true);
      const department = empData?.department || "";

      // Send state and department as query parameters
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/common/approved-installation-excel`,
        {
          params: {
            state: selectedState,
            department,
          },
          responseType: "blob", // ✅ crucial for binary data
        },
      );

      // Create blob with correct MIME type for Excel
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Approved_Installations_${selectedState}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download Excel");
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trimStart());
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (gridRef.current) {
        gridRef.current.scrollTop = 0;
      }
    }
  };

  // Loading UI
  if (loading && installations.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen text-xl">
        Loading...
      </div>
    );
  }

  // Error UI
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <h1 className="text-2xl font-bold text-center mb-4">
        VERIFIED IMAGE - {selectedState.toUpperCase()}
      </h1>

      {/* Filters */}
      <div className="flex flex-wrap justify-between gap-3 mb-4">
        <div className="flex gap-2 items-center">
          <label>State:</label>
          <select
            value={selectedState}
            onChange={handleStateChange}
            className="border px-2 py-1"
          >
            {states.map((s, i) => (
              <option key={i}>{s}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Search Saral ID"
            value={searchTerm}
            onChange={handleSearchChange}
            className="border px-2 py-1"
          />

          <span className="py-1.5 px-1.5 rounded text-dark bg-yellow-500">
            Total Records: {totalRecords}
          </span>

          <button
            onClick={downloadExcel}
            className="bg-green-600 text-white px-1.5 py-1.5 rounded"
          >
            Download Excel
          </button>
        </div>
      </div>

      {/* Grid */}
      <div
        ref={gridRef}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto"
      >
        {installations.length > 0 ? (
          installations.map((item) => (
            <div
              key={item.installationId}
              className="bg-white p-3 rounded shadow"
            >
              <div className="flex justify-between">
                <h3>Saral ID: {item.farmerSaralId || "N/A"}</h3>

                {item.stage && (
                  <span
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      item.stage === "Approved By VT-2"
                        ? "bg-green-100 text-green-800"
                        : item.stage === "Rejected By VT-2"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100"
                    }`}
                  >
                    {item.stage === "Approved By VT-2" && <FaCheck size={12} />}
                    {item.stage === "Rejected By VT-2" && <FaBan size={12} />}
                    {item.stage}
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center">No Data Found</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-3 mt-4">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="bg-gray-300 px-3 py-1"
          >
            Prev
          </button>

          <span>
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="bg-gray-300 px-3 py-1"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default VerifiedImageHistory;
